'use client';

import { useCallback, useSyncExternalStore } from 'react';

import { useInitialCompactViewport } from '@/components/responsive/InitialCompactViewportProvider';

const COMPACT_QUERY = '(max-width: 767px)';

const getMatchMedia = () => {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(COMPACT_QUERY) : null;
};

const subscribeViewport = (onStoreChange: () => void) => {
    const mq = getMatchMedia();

    if (!mq) {
        return () => undefined;
    }

    const handler = () => onStoreChange();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
};

const getSnapshot = (): boolean => {
    const mq = getMatchMedia();
    return mq ? mq.matches : false;
};

/**
 * Compact dashboard breakpoint (matches SCSS `$dashboard-compact-breakpoint` and the browser resize
 * listener). Uses `useSyncExternalStore` so the first client paint matches the SSR-resolved hint
 * (the request `User-Agent`-derived compact value plumbed through `InitialCompactViewportProvider`),
 * then updates synchronously with no delay — avoids the desktop layout flash that mobile UAs used
 * to see before hydration finished.
 */
export const useDashboardCompactViewport = (): boolean => {
    const initialIsCompact = useInitialCompactViewport();
    const getServerSnapshot = useCallback((): boolean => initialIsCompact, [initialIsCompact]);

    return useSyncExternalStore(subscribeViewport, getSnapshot, getServerSnapshot);
};
